import type { UseFormReturn } from 'react-hook-form';
import { Labeled, TextInputField } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IResetPasswordFormValues } from './ResetPasswordForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { AppRegionSelector } from '~/components/organisms/fields/AppRegionSelector';

const PASSWORD_MIN_LENGTH = 6;

export type IResetPasswordFormMainSectionProps = IContainerProps & {
  mode: 'request-change' | 'change';
  form: UseFormReturn<IResetPasswordFormValues>;
};

export const ResetPasswordFormMainSection: React.FC<
  IResetPasswordFormMainSectionProps
> = (props) => {
  const { sx, mode, form } = props;
  const { t } = useTranslation();

  return (
    <FormSection sx={sx}>
      <Labeled
        label={t('common.emailAddress')}
        supportingText={t('ResetPasswordFormMainSection.email-supporting-text')}
        required
      >
        <FormField
          name="email"
          control={form.control}
          as={TextInputField}
          forwardError
          type="email"
          autoComplete="email"
          autoCapitalize="off"
        />
      </Labeled>
      <Labeled label={t('common.region')} required>
        <FormField
          name="appRegion"
          control={form.control}
          as={AppRegionSelector}
          forwardError
        />
      </Labeled>
      {mode === 'change' ? (
        <>
          <Labeled
            label={t('ResetPasswordFormMainSection.verification-code')}
            required
          >
            <FormField
              name="token"
              control={form.control}
              as={TextInputField}
              forwardError
            />
          </Labeled>
          <Labeled
            label={t('ResetPasswordFormMainSection.new-password')}
            required
          >
            <FormField
              name="password"
              control={form.control}
              as={TextInputField}
              forwardError
              rules={{
                minLength: {
                  value: PASSWORD_MIN_LENGTH,
                  message: t(
                    'ResetPasswordFormMainSection.password-too-short',
                    { minLength: PASSWORD_MIN_LENGTH },
                  ),
                },
              }}
              type="password"
              autoComplete="new-password"
            />
          </Labeled>
          <Labeled
            label={t('ResetPasswordFormMainSection.confirm-new-password')}
            required
          >
            <FormField
              name="passwordConfirmation"
              control={form.control}
              as={TextInputField}
              forwardError
              rules={{
                validate: (value) => {
                  if (form.watch('password') !== value) {
                    return t(
                      'ResetPasswordFormMainSection.passwords-do-not-match',
                    );
                  }
                },
              }}
              type="password"
              autoComplete="new-password"
            />
          </Labeled>
        </>
      ) : null}
    </FormSection>
  );
};
